import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_ENDPOINT } from "../../../../services/api/resources";
import classes from "./charts.module.css";
import HeaderTitle from "../../../../components/common/HeaderTitle/HeaderTitle";
import SearchBar from "../../../../components/common/SearchBar/SearchBar";
import { Box, IconButton, TablePagination } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useSelector } from "react-redux";

const getStatusText = (isApproved, isAccountApproved, isFinApproved) => {
  if (
    String(isApproved) === "1" &&
    String(isAccountApproved) === "1" &&
    String(isFinApproved) === "1"
  ) {
    return "Approved";
  }
  if (String(isApproved) === "3") {
    return "Rejected";
  }
  return "Awaiting";
};

const normalizeText = (value) => String(value || "").toLowerCase().trim();

const getInvoiceStatusLabel = (invoice) =>
  invoice?.status_label ||
  getStatusText(
    invoice?.is_approved,
    invoice?.is_account_approved,
    invoice?.is_fin_approved
  );

function PaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
    onPageChange(event, lastPage);
  };

  const isBackDisabled = page === 0;
  const isNextDisabled = page >= Math.ceil(count / rowsPerPage) - 1;

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={isBackDisabled} aria-label="first page" size="medium" sx={{ p: 1.25 }}>
        {theme.direction === "rtl" ? <LastPageIcon sx={{ fontSize: 24 }} /> : <FirstPageIcon sx={{ fontSize: 24 }} />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={isBackDisabled} aria-label="previous page" size="medium" sx={{ p: 1.25 }}>
        {theme.direction === "rtl" ? <KeyboardArrowRight sx={{ fontSize: 24 }} /> : <KeyboardArrowLeft sx={{ fontSize: 24 }} />}
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={isNextDisabled} aria-label="next page" size="medium" sx={{ p: 1.25 }}>
        {theme.direction === "rtl" ? <KeyboardArrowLeft sx={{ fontSize: 24 }} /> : <KeyboardArrowRight sx={{ fontSize: 24 }} />}
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={isNextDisabled} aria-label="last page" size="medium" sx={{ p: 1.25 }}>
        {theme.direction === "rtl" ? <FirstPageIcon sx={{ fontSize: 24 }} /> : <LastPageIcon sx={{ fontSize: 24 }} />}
      </IconButton>
    </Box>
  );
}

const InvoiceStatusTable = () => {
  const Data = useSelector((state) => state.loginData.value);
  const user_token = Data?.user?.token || Data?.user_token;
  const [vendorInvoiceData, setVendorInvoiceData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [loading, setLoading] = useState(true);

  const rowsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.post(API_ENDPOINT.get_all_invoices, {
          org_token: user_token,
          page: currentPage + 1,
        });
        setVendorInvoiceData(
          res?.data?.invoices || res?.data?.results?.invoices || []
        );
        setTotalCount(Number(res?.data?.total_count || res?.data?.count || 0));
      } catch (error) {
        console.error("Error fetching invoice data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, user_token]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  };

  const filteredData = vendorInvoiceData.filter((invoice) => {
    const query = normalizeText(searchTerm);
    const nameMatch = normalizeText(invoice?.vendor_name).includes(query);
    const invoiceMatch = normalizeText(invoice?.vendor_code).includes(query);
    const amountMatch = normalizeText(invoice?.amount).includes(query);

    const status = getInvoiceStatusLabel(invoice);

    const statusMatch =
      normalizeText(statusFilter) === "all" ||
      normalizeText(status) === normalizeText(statusFilter);

    return (nameMatch || invoiceMatch || amountMatch) && statusMatch;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;

    if (sortConfig.key === "status") {
      const statusA = getInvoiceStatusLabel(a);
      const statusB = getInvoiceStatusLabel(b);
      return sortConfig.direction === "asc"
        ? statusA.localeCompare(statusB)
        : statusB.localeCompare(statusA);
    }

    if (sortConfig.key === "amount") {
      const amountA = Number(a.amount || 0);
      const amountB = Number(b.amount || 0);
      return sortConfig.direction === "asc"
        ? amountA - amountB
        : amountB - amountA;
    }

    const valA = a[sortConfig.key];
    const valB = b[sortConfig.key];

    if (typeof valA === "string" || typeof valB === "string") {
      return sortConfig.direction === "asc"
        ? String(valA || "").localeCompare(String(valB || ""))
        : String(valB || "").localeCompare(String(valA || ""));
    }

    if (typeof valA === "number" || typeof valB === "number") {
      return sortConfig.direction === "asc"
        ? Number(valA || 0) - Number(valB || 0)
        : Number(valB || 0) - Number(valA || 0);
    }

    return 0;
  });

  const currentItems = sortedData;

  return (
    <div>
      <HeaderTitle titleText={"Invoice Status"} />

      <div className={classes.filterWrapper}>
        <SearchBar
          customClass={classes.tableSearch}
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(0);
          }}
        />

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(0);
          }}
          className={classes.filterSelect}
        >
          <option value="All">All Status</option>
          <option value="Approved">Approved</option>
          <option value="Pending">Awaiting</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      <table className={`${classes.statustable} ${classes.buyerHeaderTable}`}>
        <thead>
          <tr>
            <th onClick={() => handleSort("vendor_code")}>
              Supplier Invoice ID{sortConfig.key === "vendor_code" && (sortConfig.direction === "asc" ? "^" : "v")}
            </th>
            <th onClick={() => handleSort("vendor_name")}>
              Merchant Name {sortConfig.key === "vendor_name" && (sortConfig.direction === "asc" ? "^" : "v")}
            </th>
            <th onClick={() => handleSort("amount")}>
              Amount {sortConfig.key === "amount" && (sortConfig.direction === "asc" ? "^" : "v")}
            </th>
            <th onClick={() => handleSort("status")}>
              Status {sortConfig.key === "status" && (sortConfig.direction === "asc" ? "^" : "v")}
            </th>
            <th>Signed On</th>
            <th>Approved By</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="7" className={classes.loadingRow}>
                Loading data...
              </td>
            </tr>
          ) : currentItems.length === 0 ? (
            <tr>
              <td colSpan="7" className="no-data">
                No data found
              </td>
            </tr>
          ) : (
            currentItems.map((invoice) => {
              const status = getInvoiceStatusLabel(invoice);

              return (
                <tr key={invoice.invoice_token || invoice.id}>
                  <td>{invoice.vendor_code || "-"}</td>
                  <td>{invoice.vendor_name || "-"}</td>
                  <td>{"\u20B9"}{Number(invoice.amount || 0).toLocaleString("en-IN")}</td>
                  {/* <td>{status}</td> */}
                  <td>{status}</td>
                  <td>
                    {invoice.esign_status === "Signed"
                      ? new Date(invoice.created_on).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>{invoice.account_approved === true ? "Finance Team" : "-"}</td>
                  <td>
                    <button
                      className="action-btn"
                      onClick={() => {
                        setPdfBase64(invoice.pdf);
                        setPdfModalOpen(true);
                      }}
                      style={{textDecoration:"underline",color:"blue"}}
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {(searchTerm || statusFilter !== "All"
        ? filteredData.length > rowsPerPage
        : totalCount > rowsPerPage) && (
        <TablePagination
          component="div"
          count={
            searchTerm || statusFilter !== "All"
              ? filteredData.length
              : totalCount
          }
          page={currentPage}
          onPageChange={(event, newPage) => setCurrentPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={() => {}}
          rowsPerPageOptions={[rowsPerPage]}
          showFirstButton
          showLastButton
          ActionsComponent={PaginationActions}
          sx={{
            "& .MuiTablePagination-toolbar": { fontSize: "14px" },
            "& .MuiTablePagination-selectLabel": { fontSize: "14px" },
            "& .MuiTablePagination-displayedRows": { fontSize: "14px" },
            "& .MuiTablePagination-select": { fontSize: "14px" },
          }}
        />
      )}

      {pdfModalOpen && (
        <div className={classes.pdfmodaloverlay} onClick={() => setPdfModalOpen(false)}>
          <div className={classes.pdfmodalcontent} onClick={(e) => e.stopPropagation()}>
            <button className={classes.closebtn} onClick={() => setPdfModalOpen(false)}>
              X
            </button>
            {pdfBase64 ? (
              <iframe
                title="Invoice PDF"
                src={`data:application/pdf;base64,${pdfBase64}`}
                width="100%"
                height="600px"
                style={{ border: "none" }}
              />
            ) : (
              <p>PDF not available</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceStatusTable;
